import { CFD_POS_PAYMENT_FLOWS, USER_PROGRESS } from '../../VNCustomerFacingDisplay/Enums'
import { getCurrentPaymentFlow } from '../../VNCustomerFacingDisplay/Selectors'
import {
  setCardAuthStepOne,
  setCardAuthStepTwo,
  setConfirmPaymentDialog,
  setConnectionRequiredDialog,
  setCustomerPayingWithCard,
  setCustomerViewingReceiptDialog,
  setOpenCustomerEnteringTipDialog,
  setOpenCustomerPaymentDialog,
  setTransactionCompleteDialog,
  setTransactionErrorDialog,
} from '../../VNDialogs/ActionCreators'
import { MODES } from '../../VNMode/Reducer'
import { getDeviceMode } from '../../VNMode/Selectors'
import * as VNCFD_ActionTypes from '../../VNCustomerFacingDisplay/ActionTypes'
import {
  addDonationsToOrderInProgress,
  addPaymentToOrderInProgress,
  addTipOrderInProgress,
  closeNoOpTender,
  selectOrder,
  updateSavedPayment,
} from '../../actions/order'
import { bridge_hijackedExecuteSalesRequest, sendCFDScreenNav } from '../bridgeCalls/VNWebSDKDataSend'
import { getOrderInProgress } from '../../selectors/order'
import { setCurrentOrder } from '../../VNCustomerFacingDisplay/ActionCreators'
import { PAYMENT_TYPES_INTERNAL } from '../../utils/paymentTypes'
import {
  getCardAuthStepOne,
  getCustomerEnteringTipDialogOpen,
  getCustomerPayingWithCard,
  getCustomerPaymentDialogOpen,
} from '../../VNDialogs/Selectors'
import { isTabbed } from '../../utils/orderStates'
import { CFD_SCREEN } from '../../VNCustomerFacingDisplay/Reducer'
import { getNetworkAvailableValue } from '../../utils/networkConnected'
import { APPLICATION_STRINGS } from '../../strings'
import { setRoute } from '../../VNRoutes/ActionCreators'

const TRANSACTION_ERROR_DELAY = 3000

export const userPaymentProgressHandler = (state, dispatch, data) => {
  const currentPaymentFlow = getCurrentPaymentFlow(state)
  const deviceMode = getDeviceMode(state)
  const order = getOrderInProgress(state)
  const customerPayingWithCard = getCustomerPayingWithCard(state)
  const networkAvailable = getNetworkAvailableValue(state)
  const enteringTipDialog = getCustomerEnteringTipDialogOpen(state)
  const customerPaymentDialogOpen = getCustomerPaymentDialogOpen(state)
  const authStepOne = getCardAuthStepOne(state)

  if (data.flow === 'VIEWING_RECEIPT' && currentPaymentFlow !== CFD_POS_PAYMENT_FLOWS.UNSET && data.freeItem === 'true') {
    dispatch(setOpenCustomerEnteringTipDialog({ open: false }))
    dispatch(setConfirmPaymentDialog(false))
    dispatch(setTransactionCompleteDialog(false))

    if (isTabbed(order) || order?.isTab) {
      dispatch(closeNoOpTender({ orderUuid: order.uuid, orderNumber: order.orderNumber }))
    }
  }

  // early out if we are offline and the order was created online and is tabbed
  if (
    !networkAvailable &&
    order?.wasCreatedInStadium &&
    isTabbed(order) &&
    (enteringTipDialog?.get('open') || customerPaymentDialogOpen)
  ) {
    dispatch(setConnectionRequiredDialog({ open: true, subtext: APPLICATION_STRINGS.INTERNET_CONNECTION_REQUIRED }))
    dispatch(selectOrder(order.uuid))
    sendCFDScreenNav(CFD_SCREEN.IDLE)
    dispatch(setConfirmPaymentDialog(false))
    dispatch(setOpenCustomerEnteringTipDialog({ open: false }))
    dispatch(setOpenCustomerPaymentDialog({ open: false }))

    dispatch(setRoute('/orders'))
    return
  }

  if (data.flow === 'VIEWING_RECEIPT' && data?.tabbed === 'true') {
    dispatch(setOpenCustomerEnteringTipDialog({ open: false }))
    if (data?.tenderData?.paymentType === PAYMENT_TYPES_INTERNAL.CREDIT_CARD) {
      // tabbed payment
      // dispatch is too slow to update the order so we are manually getting the amount in cents
      const amountInCents =
        parseInt(order.amountInCents) + parseInt(data.tipInCents ?? 0) + parseInt(data.charityAmountInCents ?? 0)

      // free
      if (amountInCents === 0) {
        dispatch(closeNoOpTender({ orderUuid: order.uuid, orderNumber: order.orderNumber }))
        dispatch(setConfirmPaymentDialog(false))
        sendCFDScreenNav(CFD_SCREEN.RECEIPT)
        return
      }

      bridge_hijackedExecuteSalesRequest(amountInCents, order.orderNumber, amountInCents)
      dispatch(setCustomerPayingWithCard({ open: true, amount: amountInCents }))
      dispatch(addTipOrderInProgress(parseInt(data.tipInCents ?? 0)))
      dispatch(addDonationsToOrderInProgress(parseInt(data.charityAmountInCents ?? 0)))
      // For transaction errors
      dispatch(setCurrentOrder({ amount: amountInCents }))
      return
    }

    dispatch(setConfirmPaymentDialog(false))
    dispatch(setTransactionCompleteDialog(false))

    if (data?.tenderData?.paymentType === PAYMENT_TYPES_INTERNAL.CUSTOM_TENDER) {
      const amountInCents = parseInt(order.amountInCents) + parseInt(data.tipInCents ?? 0)

      dispatch(
        addPaymentToOrderInProgress({
          tenderAmountInCents: parseInt(order.amountInCents),
          paymentType: data.tenderData.name,
          shortDescription: data.tenderData.displayName,
          tipAmountInCents: parseInt(data.tipInCents ?? 0),
        }),
      )

      sendCFDScreenNav(CFD_SCREEN.RECEIPT, {
        cartTotals: { uuid: order?.uuid, remainingTicketBalance: order?.remainingTicketBalance },
        transaction: { amount: amountInCents ? amountInCents / 100 : 0 },
      })
      return
    }

    // tabbed saved card
    dispatch(
      updateSavedPayment({
        tipAmountInCents: parseInt(data.tipInCents ?? 0),
        charityAmountInCents: parseInt(data.charityAmountInCents ?? 0),
      }),
    )
    sendCFDScreenNav(CFD_SCREEN.RECEIPT)
  }

  if (
    currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.QR_PAY ||
    currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.GIFT_CARD ||
    currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.TICKET
  ) {
    if (data.flow === 'TRANSACTION_COMPLETE') {
      dispatch(setCustomerViewingReceiptDialog({ open: false }))
      dispatch(setTransactionCompleteDialog(true))
    } else if (data.flow === 'TRANSACTION_ERROR') {
      setTimeout(() => {
        dispatch(setTransactionErrorDialog({ open: true, subTitle: data.subTitle }))
      }, TRANSACTION_ERROR_DELAY)
      return
    }
  } else if (deviceMode === MODES.POS && currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.SPLIT_PAY) {
    if (data.flow === 'TRANSACTION_ERROR') {
      setTimeout(() => {
        dispatch(setTransactionErrorDialog({ open: true, subTitle: data.subTitle }))
      }, TRANSACTION_ERROR_DELAY)
      return
    }
  } else {
    if (data.flow === 'TRANSACTION_ERROR') {
      const authStepOneOpen = authStepOne?.get && authStepOne.get('open')
      // legacy cfd payment flow has its own transaction error dialog so we need to check if the new dialog is open first.
      if (customerPayingWithCard.get('open') || authStepOneOpen) {
        setTimeout(() => {
          dispatch(setTransactionErrorDialog({ open: true, subTitle: data.subTitle }))
        }, TRANSACTION_ERROR_DELAY)
        return
      }
    }
  }

  switch (data.title) {
    case USER_PROGRESS.CARD_PAYMENT.AUTHORIZED_CARD.title:
      setCardAuthStepTwo({ open: true, amount: data.amount })
      setCardAuthStepOne({ open: false })
      break

    default:
      break
  }

  dispatch({
    type: VNCFD_ActionTypes.VNCFD_USER_PAYMENT_PROGRESS,
    data: data,
  })
}
